import {
    Box,
    Chip,
    IconButton,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { formatRange } from '../../components/charts/chartFormat';
import { RESOLUTION_OPTIONS } from '../../components/charts/convergence/constants';
import { COARSE, MIN_TARGET_PX } from '../../../../root/touchTheme';
import { CompactChartToolbar } from '../../event/chart/CompactChartToolbar';
import { useCompactChart } from '../../event/chart/useCompactChart';
import { ASSET_QUICK_RANGES, compactRangesFor } from '../window';
import type { PriceMode } from './priceSeries';

const AUTO = 'auto';

interface AssetToolbarProps {
    displayRange: [Date, Date];
    activeRange: string | null;
    onQuickRange: (id: string) => void;
    mode: PriceMode;
    onModeChange: (mode: PriceMode) => void;
    resolutionChoice: number | null;
    resolutionLabel: string;
    onResolutionChange: (choice: number | null) => void;
    volumeAvailable: boolean;
    showVolume: boolean;
    onToggleVolume: () => void;
    fullscreen: boolean;
    onToggleFullscreen: () => void;
    /** Decides which phone ranges make sense (a daily macro series has no 1H). */
    assetType?: string | null;
}

const toggleGroupSx = {
    height: 26,
    '& .MuiToggleButton-root': {
        py: '2px',
        px: 1,
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'none' as const,
        borderColor: 'divider',
    },
};

function DesktopAssetToolbar(props: AssetToolbarProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 0.5,
            }}
        >
            <Box sx={{ flex: 1 }} />
            <Typography
                sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    color:
                        props.activeRange === null
                            ? 'text.primary'
                            : 'text.secondary',
                    fontWeight: props.activeRange === null ? 600 : 400,
                }}
            >
                {formatRange(props.displayRange)}
            </Typography>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={props.activeRange}
                onChange={(_, value: string | null) => {
                    if (value) props.onQuickRange(value);
                }}
                sx={toggleGroupSx}
            >
                {ASSET_QUICK_RANGES.map(q => (
                    <ToggleButton
                        key={q.id}
                        value={q.id}
                        aria-label={`Time range: ${q.label}`}
                    >
                        {q.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {props.volumeAvailable && (
                <Chip
                    label="Volume"
                    size="small"
                    onClick={props.onToggleVolume}
                    color={props.showVolume ? 'primary' : 'default'}
                    variant={props.showVolume ? 'filled' : 'outlined'}
                    sx={{ height: 22, fontSize: '10px', fontWeight: 600 }}
                />
            )}
            <ToggleButtonGroup
                size="small"
                exclusive
                value={props.mode}
                onChange={(_, value: PriceMode | null) => {
                    if (value) props.onModeChange(value);
                }}
                sx={toggleGroupSx}
            >
                <ToggleButton value="line">Line</ToggleButton>
                <ToggleButton value="candles">Candles</ToggleButton>
            </ToggleButtonGroup>
            <Select
                value={props.resolutionChoice ?? AUTO}
                onChange={event => {
                    const value = event.target.value;
                    props.onResolutionChange(
                        value === AUTO ? null : Number(value)
                    );
                }}
                size="small"
                variant="outlined"
                renderValue={value =>
                    value === AUTO
                        ? `Auto (${props.resolutionLabel})`
                        : props.resolutionLabel
                }
                sx={{
                    height: 26,
                    // in a row of 44 px buttons on a touch screen
                    [COARSE]: { height: MIN_TARGET_PX },
                    fontSize: '11px',
                    fontWeight: 600,
                    '& .MuiSelect-select': { py: '2px', px: 1 },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                    },
                }}
            >
                <MenuItem value={AUTO} sx={{ fontSize: '12px' }}>
                    Auto
                </MenuItem>
                {RESOLUTION_OPTIONS.map(option => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                        sx={{ fontSize: '12px' }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            <Tooltip
                title={props.fullscreen ? 'Exit full screen' : 'Full screen'}
            >
                <IconButton
                    size="small"
                    onClick={props.onToggleFullscreen}
                    sx={{ p: 0.25 }}
                >
                    {props.fullscreen ? (
                        <FullscreenExitIcon sx={{ fontSize: 18 }} />
                    ) : (
                        <FullscreenIcon sx={{ fontSize: 18 }} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

export function AssetToolbar(props: AssetToolbarProps) {
    const compact = useCompactChart();
    if (!compact) return <DesktopAssetToolbar {...props} />;
    return (
        <CompactChartToolbar
            ranges={compactRangesFor(props.assetType)}
            activeRange={props.activeRange}
            onRange={props.onQuickRange}
            options={{
                view: {
                    value: props.mode,
                    options: [
                        { value: 'line', label: 'Line' },
                        { value: 'candles', label: 'Candles' },
                    ],
                    onChange: props.onModeChange,
                },
                resolution: {
                    choice: props.resolutionChoice,
                    label: props.resolutionLabel,
                    onChange: props.onResolutionChange,
                },
                volume: props.volumeAvailable
                    ? {
                          shown: props.showVolume,
                          onToggle: props.onToggleVolume,
                      }
                    : undefined,
            }}
            fullscreen={props.fullscreen}
            onToggleFullscreen={props.onToggleFullscreen}
        />
    );
}
